/**
 * All Stocks Page - Browse all tradable stocks from Alpaca
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { alpacaAPI, analysisAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Search, TrendingUp, TrendingDown, ShoppingCart, Filter,
  ChevronLeft, ChevronRight, Grid, List, RefreshCw, X, Brain,
  Newspaper, ExternalLink, Activity, BarChart3, AlertTriangle, Zap
} from 'lucide-react';

// ============ STOCK CARD ============
const StockCard = ({ stock, onAnalyze, onTrade }) => {
  const isPositive = (stock.change || 0) >= 0;

  return (
    <div className="card p-4 hover:border-accent-blue transition-all cursor-pointer" onClick={() => onAnalyze(stock)}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-mono text-lg font-semibold">{stock.symbol}</div>
          <div className="text-text-muted text-xs truncate max-w-[150px]">{stock.name}</div>
        </div>
        {stock.category && (
          <span className="badge badge-blue text-xs">{stock.category}</span>
        )}
      </div>

      {stock.price > 0 ? (
        <>
          <div className="font-mono text-xl font-semibold mb-1">${stock.price?.toFixed(2)}</div>
          <div className={`flex items-center gap-1 text-sm mb-3 ${isPositive ? 'text-accent-green' : 'text-accent-red'}`}>
            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="font-mono">{isPositive ? '+' : ''}{stock.change?.toFixed(2)}%</span>
          </div>
        </>
      ) : (
        <div className="text-text-muted text-sm mb-3">Price loading...</div>
      )}

      <div className="flex gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onTrade(stock, 'buy'); }}
          className="flex-1 py-2 px-3 rounded-lg bg-accent-green/15 text-accent-green text-sm font-medium hover:bg-accent-green/25 transition-colors flex items-center justify-center gap-1"
        >
          <ShoppingCart className="w-3 h-3" />Buy
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onTrade(stock, 'sell'); }}
          className="flex-1 py-2 px-3 rounded-lg bg-accent-red/15 text-accent-red text-sm font-medium hover:bg-accent-red/25 transition-colors flex items-center justify-center gap-1"
        >
          <TrendingDown className="w-3 h-3" />Sell
        </button>
      </div>
    </div>
  );
};

// ============ STOCK ROW (List View) ============
const StockRow = ({ stock, onAnalyze, onTrade }) => {
  const isPositive = (stock.change || 0) >= 0;

  return (
    <tr className="hover:bg-bg-tertiary/50 cursor-pointer" onClick={() => onAnalyze(stock)}>
      <td className="font-mono font-semibold">{stock.symbol}</td>
      <td className="text-text-secondary truncate max-w-[200px]">{stock.name}</td>
      <td className="text-right font-mono">{stock.price > 0 ? `$${stock.price.toFixed(2)}` : '-'}</td>
      <td className="text-right">
        {stock.price > 0 ? (
          <span className={`font-mono ${isPositive ? 'text-accent-green' : 'text-accent-red'}`}>
            {isPositive ? '+' : ''}{stock.change?.toFixed(2)}%
          </span>
        ) : '-'}
      </td>
      <td className="text-right font-mono text-text-muted">
        {stock.volume ? (stock.volume / 1000000).toFixed(1) + 'M' : '-'}
      </td>
      <td>{stock.category && <span className="badge badge-blue text-xs">{stock.category}</span>}</td>
      <td className="text-center">
        <div className="flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onTrade(stock, 'buy')} className="px-3 py-1 rounded bg-accent-green/15 text-accent-green text-xs font-medium hover:bg-accent-green/25">Buy</button>
          <button onClick={() => onTrade(stock, 'sell')} className="px-3 py-1 rounded bg-accent-red/15 text-accent-red text-xs font-medium hover:bg-accent-red/25">Sell</button>
        </div>
      </td>
    </tr>
  );
};

// ============ TRADE MODAL ============
const TradeModal = ({ stock, side, onClose, onSuccess }) => {
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('1');
  const [limitPrice, setLimitPrice] = useState(stock?.price?.toFixed(2) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) { setError('Enter valid quantity'); return; }
    if (orderType === 'limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) { setError('Enter valid limit price'); return; }

    setLoading(true);
    setError('');

    try {
      const order = { symbol: stock.symbol, qty: parseFloat(quantity), side, type: orderType, timeInForce: 'day' };
      if (orderType === 'limit') order.limitPrice = parseFloat(limitPrice);

      await alpacaAPI.placeOrder(order);
      toast.success(`${side.toUpperCase()} order placed for ${quantity} shares of ${stock.symbol}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order');
    } finally { setLoading(false); }
  };

  const estimatedTotal = parseFloat(quantity || 0) * parseFloat(orderType === 'limit' ? limitPrice : stock?.price || 0);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content p-6 max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${side === 'buy' ? 'bg-accent-green/15' : 'bg-accent-red/15'}`}>
              {side === 'buy' ? <ShoppingCart className="w-6 h-6 text-accent-green" /> : <TrendingDown className="w-6 h-6 text-accent-red" />}
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold">{side === 'buy' ? 'Buy' : 'Sell'} {stock?.symbol}</h2>
              <p className="text-text-secondary text-sm">{stock?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-secondary p-2 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {stock?.price > 0 && (
          <div className="card p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Current Price</span>
              <span className="font-mono text-xl font-bold">${stock?.price?.toFixed(2)}</span>
            </div>
          </div>
        )}

        {error && <div className="mb-4 p-3 rounded-lg bg-accent-red/10 border border-accent-red/20"><p className="text-accent-red text-sm">{error}</p></div>}

        <div className="mb-5">
          <label className="block text-text-secondary text-sm mb-2">Order Type</label>
          <div className="flex gap-2">
            {['market', 'limit'].map(type => (
              <button key={type} onClick={() => setOrderType(type)} className={`flex-1 py-3 px-4 rounded-lg border transition-all ${orderType === type ? 'bg-accent-blue/15 border-accent-blue text-accent-blue' : 'border-border-color text-text-secondary'}`}>
                <div className="font-medium capitalize">{type}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-text-secondary text-sm mb-2">Quantity</label>
          <input type="number" min="1" step="1" value={quantity} onChange={e => setQuantity(e.target.value)} className="input font-mono text-lg" />
        </div>

        {orderType === 'limit' && (
          <div className="mb-5">
            <label className="block text-text-secondary text-sm mb-2">Limit Price ($)</label>
            <input type="number" min="0.01" step="0.01" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} className="input font-mono text-lg" />
          </div>
        )}

        <div className="card p-4 mb-6 bg-bg-tertiary">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Estimated Total</span>
            <span className="font-mono text-2xl font-bold">${estimatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading} className={`w-full py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 ${side === 'buy' ? 'bg-accent-green text-bg-primary' : 'bg-accent-red text-white'}`}>
          {loading ? 'Processing...' : `${side === 'buy' ? 'Buy' : 'Sell'} ${quantity || 0} Shares`}
        </button>
      </div>
    </div>
  );
};

// ============ ANALYSIS PANEL ============
const AnalysisPanel = ({ stock, onClose, onTrade }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalysis = async () => {
      setLoading(true);
      try {
        const [techRes, summaryRes] = await Promise.all([
          analysisAPI.getTechnical(stock.symbol).catch(() => null),
          analysisAPI.getSummary(stock.symbol).catch(() => null),
        ]);
        setAnalysis({
          technical: techRes?.data,
          summary: summaryRes?.data,
        });
      } catch (err) {
        console.error('Analysis error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [stock.symbol]);

  return (
    <div className="fixed top-0 right-0 w-[450px] h-screen bg-bg-secondary border-l border-border-color p-6 overflow-y-auto z-50 animate-slide-in-right">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="font-display text-2xl font-bold">{stock.symbol}</div>
          <div className="text-text-secondary">{stock.name}</div>
        </div>
        <button onClick={onClose} className="btn-secondary p-2.5 rounded-xl"><X className="w-5 h-5" /></button>
      </div>

      {stock.price > 0 && (
        <div className="card p-5 mb-5">
          <div className="font-mono text-3xl font-bold">${stock.price?.toFixed(2)}</div>
          <div className={`flex items-center gap-2 mt-1 ${stock.change >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
            {stock.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="font-mono">{stock.change >= 0 ? '+' : ''}{stock.change?.toFixed(2)}%</span>
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-5">
        <button onClick={() => onTrade(stock, 'buy')} className="flex-1 py-3 rounded-xl bg-accent-green text-bg-primary font-semibold flex items-center justify-center gap-2">
          <ShoppingCart className="w-5 h-5" />Buy
        </button>
        <button onClick={() => onTrade(stock, 'sell')} className="flex-1 py-3 rounded-xl bg-accent-red text-white font-semibold flex items-center justify-center gap-2">
          <TrendingDown className="w-5 h-5" />Sell
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2].map(i => <div key={i} className="card p-5"><div className="h-20 loading-shimmer rounded" /></div>)}</div>
      ) : analysis?.summary ? (
        <>
          <div className="card p-5 mb-5 text-center">
            <div className="text-text-muted text-sm mb-2">AI SCORE</div>
            <div className={`font-display text-5xl font-bold ${analysis.summary.overallScore >= 65 ? 'gradient-text-green' : analysis.summary.overallScore >= 45 ? 'gradient-text-blue' : 'gradient-text-red'}`}>
              {analysis.summary.overallScore}%
            </div>
            <div className={`inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-semibold ${
              analysis.summary.recommendation?.includes('BUY') ? 'bg-accent-green/15 text-accent-green' :
              analysis.summary.recommendation === 'HOLD' ? 'bg-accent-blue/15 text-accent-blue' :
              'bg-accent-red/15 text-accent-red'
            }`}>
              {analysis.summary.recommendation}
            </div>
          </div>

          {analysis.summary.analysis?.technical && (
            <div className="card p-4 mb-4">
              <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-accent-blue" /><h4 className="font-medium text-sm">Technical</h4></div>
              <p className="text-text-secondary text-sm">{analysis.summary.analysis.technical}</p>
            </div>
          )}

          {analysis.summary.risks && (
            <div className="card p-4">
              <h4 className="text-accent-red flex items-center gap-2 mb-2 text-sm"><AlertTriangle className="w-4 h-4" />Risks</h4>
              <ul className="text-text-secondary text-sm space-y-1">
                {analysis.summary.risks.slice(0, 3).map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="card p-8 text-center">
          <Brain className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-text-secondary">Analysis not available</p>
        </div>
      )}
    </div>
  );
};

// ============ MAIN PAGE ============
const AllStocks = () => {
  const [loading, setLoading] = useState(true);
  const [stocks, setStocks] = useState([]);
  const [totalStocks, setTotalStocks] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(0);
  const [selectedStock, setSelectedStock] = useState(null);
  const [tradeModal, setTradeModal] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const ITEMS_PER_PAGE = 50;

  // Fetch popular stocks with prices
  const fetchStocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await alpacaAPI.getPopularAssets();
      setStocks(res.data.stocks || []);
      setCategories(res.data.categories || []);
      setTotalStocks(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch stocks:', err);
      toast.error('Failed to load stocks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Search all assets
  const searchAssets = useCallback(async (query) => {
    if (!query) {
      fetchStocks();
      return;
    }
    
    setLoading(true);
    try {
      const res = await alpacaAPI.getAssets(query, 100, 0);
      // Get prices for search results
      const symbols = res.data.assets.slice(0, 20).map(a => a.symbol);
      if (symbols.length > 0) {
        try {
          const snapshotRes = await alpacaAPI.getSnapshot(symbols.join(','));
          const snapshots = snapshotRes.data;
          
          const stocksWithPrices = res.data.assets.map(asset => {
            const snap = snapshots[asset.symbol];
            const price = snap?.latestTrade?.p || 0;
            const prevClose = snap?.prevDailyBar?.c || price;
            const change = prevClose ? ((price - prevClose) / prevClose) * 100 : 0;
            return { ...asset, price, change };
          });
          setStocks(stocksWithPrices);
        } catch (e) {
          setStocks(res.data.assets.map(a => ({ ...a, price: 0, change: 0 })));
        }
      } else {
        setStocks([]);
      }
      setTotalStocks(res.data.total);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchStocks]);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 1) {
        searchAssets(searchQuery);
      } else if (searchQuery === '') {
        fetchStocks();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchAssets, fetchStocks]);

  // Filter by category
  const filteredStocks = activeCategory === 'all' 
    ? stocks 
    : stocks.filter(s => s.category === activeCategory);

  // Paginate
  const paginatedStocks = filteredStocks.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredStocks.length / ITEMS_PER_PAGE);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStocks();
  };

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="btn-secondary p-2.5 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-display text-2xl font-bold">All Stocks</h1>
            <p className="text-text-secondary">{totalStocks.toLocaleString()} tradable assets available</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} disabled={refreshing} className="btn-secondary p-2.5 rounded-xl">
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex bg-bg-tertiary rounded-lg p-1">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-bg-card text-text-primary' : 'text-text-muted'}`}>
              <Grid className="w-5 h-5" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-bg-card text-text-primary' : 'text-text-muted'}`}>
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-bg-card rounded-xl border border-border-color">
          <Search className="w-5 h-5 text-text-muted" />
          <input
            type="text"
            placeholder="Search by symbol or name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            className="bg-transparent border-none outline-none flex-1 text-text-primary placeholder:text-text-muted"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-text-muted hover:text-text-secondary">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {!searchQuery && categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setActiveCategory('all'); setPage(0); }}
              className={`px-4 py-2 rounded-lg border transition-all ${activeCategory === 'all' ? 'bg-accent-blue/15 border-accent-blue text-accent-blue' : 'border-border-color text-text-secondary hover:border-text-muted'}`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setPage(0); }}
                className={`px-4 py-2 rounded-lg border transition-all capitalize ${activeCategory === cat ? 'bg-accent-blue/15 border-accent-blue text-accent-blue' : 'border-border-color text-text-secondary hover:border-text-muted'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array(20).fill(0).map((_, i) => (
              <div key={i} className="card p-4"><div className="h-24 loading-shimmer rounded" /></div>
            ))}
          </div>
        ) : (
          <div className="card"><div className="h-96 loading-shimmer" /></div>
        )
      ) : paginatedStocks.length === 0 ? (
        <div className="card p-12 text-center">
          <Search className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No stocks found</h3>
          <p className="text-text-secondary">Try a different search term</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {paginatedStocks.map((stock, i) => (
            <StockCard key={stock.symbol} stock={stock} onAnalyze={setSelectedStock} onTrade={(s, side) => setTradeModal({ stock: s, side })} />
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th className="text-right">Price</th>
                <th className="text-right">Change</th>
                <th className="text-right">Volume</th>
                <th>Category</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStocks.map((stock, i) => (
                <StockRow key={stock.symbol} stock={stock} onAnalyze={setSelectedStock} onTrade={(s, side) => setTradeModal({ stock: s, side })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-secondary p-2.5 rounded-xl disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-text-secondary">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="btn-secondary p-2.5 rounded-xl disabled:opacity-50"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Modals */}
      {selectedStock && (
        <AnalysisPanel
          stock={selectedStock}
          onClose={() => setSelectedStock(null)}
          onTrade={(s, side) => setTradeModal({ stock: s, side })}
        />
      )}

      {tradeModal && (
        <TradeModal
          stock={tradeModal.stock}
          side={tradeModal.side}
          onClose={() => setTradeModal(null)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
};

export default AllStocks;
